import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import axios from "axios";
import styled from "styled-components";
import { useLocation, useParams } from "react-router";
import { tokens } from "../../../../public/tokens";
import PermissionsGate from "../../../../utils/permissionGate";
import useVersionModules from "./hooks/useVersionModules";
import {
  GetProgramAssignedOrNotAssignedListCancelToken,
  GetProgramAssignedOrNotAssignedListFilter,
} from "../../../../services/ProgramService";
import VersionSummaryStripContainer from "./VersionSummaryStripContainer";
import VersionModulesToolbar from "./versionModules/VersionModulesToolbar";
import VersionModulesTable from "./versionModules/VersionModulesTable";
import VersionModulesBulkActions from "./versionModules/VersionModulesBulkActions";
import ConfirmationModal from "../linkedProgrammes/ConfirmationModal";
import Modal from "../../../library/Modal";
import AvailableTables from "./AvailableTables";

/*
 * Matches the Figma dev-mode spec's "spacing/5" (20px) gap between the summary strip and
 * the toolbar/table below it. This wrapper intentionally carries NONE of the legacy
 * "custom-table-div card card-table-custom" classNames a previous version of this file
 * used - every rule those classes carry (careerLibrary.css, custom.css, programsTable.css)
 * targets react-data-table-component's own generated `.rdt_Table*`/`.rdt_Pagination`
 * markup, which this hand-built table/toolbar/pagination never renders. Keeping them added
 * nothing intentional and only risked an unrelated rule reaching in unexpectedly; this card
 * owns its own padding (zero - the toolbar, table and footer inside each own their own)
 * explicitly instead, so the table/footer sit flush with the card's edges exactly like the
 * Career Library table's own header/rows do inside its card.
 */
/*
 * Reserves room at the bottom of the page for the fixed-position bulk-action bar,
 * mirroring linkedProgrammes/LinkedProgrammes.styles.js's StyledBulkActionSpacer -
 * without it, the floating "N Modules selected" bar overlaps the table's own last
 * rows/pagination instead of the page just scrolling further to clear it.
 */
const StyledBulkActionSpacer = styled.div(({ $active = false, $height = 0 }) => ({
  width: "100%",
  height: $active
    ? `calc(${Math.max($height, 84)}px + ${tokens.globals["spacing-xl"]})`
    : "0",
  transition: "height 200ms cubic-bezier(0.16, 1, 0.3, 1)",
}));

const StyledTableWrapper = styled.div({
  margin: 0,
  marginTop: tokens.globals["spacing-5"],
  padding: 0,
  /*
   * No `overflow: hidden` here (unlike the equivalent, correctly-scoped
   * `.custom-table-div` rule this wrapper used to rely on) - the row-actions
   * dropdown menu (VersionModulesRowCells.js's StyledMenu) is an absolutely
   * positioned descendant that can extend below this card's own box for rows
   * near the bottom of the table; clipping overflow here would cut it off.
   */
  background: tokens.colors.surfaces["container-background"],
  borderRadius: tokens.globals["border-radius"],
  border: `${tokens.globals["spacing-px"]} solid ${tokens.colors.surfaces["border-divider"]}`,
  boxShadow: tokens.effects.effects.shadow.default,
});

/**
 * Version Modules screen (SPEC3): the version summary strip (with "Assign
 * Module" as its right-hand action), the redesigned modules table with
 * search/filters/sort/pagination and bulk unlink, and the existing Assign
 * Module flow (`AvailableTables`, unchanged) - now shown through the shared
 * `Modal` component instead of a jQuery-driven Bootstrap modal.
 */
const AssignedTable = (props) => {
  const { versionData } = props;
  const { id } = useParams();
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const queryVersionId = query.get("versionId");
  const queryProgrammeId = query.get("programmeId");

  const isLocked = Boolean(versionData?.lock_version_settings);

  const hook = useVersionModules({ programmeId: queryProgrammeId, versionId: queryVersionId, isLocked });

  const bulkActionsBarRef = useRef(null);
  const [bulkActionsBarHeight, setBulkActionsBarHeight] = useState(0);
  const hasSelection = hook.selectedIds.length > 0;

  useLayoutEffect(() => {
    const barNode = bulkActionsBarRef.current;

    if (!hasSelection || !barNode) {
      return undefined;
    }

    const updateHeight = () => {
      setBulkActionsBarHeight(barNode.getBoundingClientRect().height);
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(barNode);

    return () => resizeObserver.disconnect();
  }, [hasSelection]);

  /* "Assign Module" modal's own available-modules list and filters - unchanged existing flow. */
  const [availableData, setAvailableData] = useState([]);
  const [availableLoading, setAvailableLoading] = useState(false);
  const [filterDataAssigned, setFilterDataAssigned] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [isAssignModuleModalOpen, setIsAssignModuleModalOpen] = useState(false);

  useEffect(() => {
    const filterSource = axios.CancelToken.source();

    const getFilters = async () => {
      try {
        const res = await GetProgramAssignedOrNotAssignedListFilter(
          queryProgrammeId,
          "assigned",
          queryVersionId,
          filterSource.token
        );
        if (res.status === 200) {
          setFilterDataAssigned(res.data?.types);
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error("AssignedTable.js->getFilters->Error", error);
        }
      }
    };

    getFilters();

    return () => filterSource.cancel("Component unmounted");
  }, [queryProgrammeId, queryVersionId]);

  useEffect(() => {
    const cancelTokenSources = [];

    const getAvailableList = async () => {
      setAvailableLoading(true);

      cancelTokenSources.forEach((source) => source.cancel("New request made"));

      const source = axios.CancelToken.source();
      cancelTokenSources.push(source);

      try {
        const res = await GetProgramAssignedOrNotAssignedListCancelToken(
          { PId: queryProgrammeId, type: "available", version_id: queryVersionId },
          source.token
        );
        if (res.status === 200) {
          setAvailableLoading(false);
          setAvailableData(res.data.programs);
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error("AssignedTable.js->getAvailableList->Error", error);
          setAvailableLoading(false);
        }
      }
    };

    getAvailableList();

    return () => cancelTokenSources.forEach((source) => source.cancel("Component unmounted"));
  }, [refresh, queryProgrammeId, queryVersionId]);


  const handleAssignModuleRefresh = () => {
    setRefresh((current) => !current);
    hook.refresh();
  };

  return (
    <div>
      <PermissionsGate scopes={["pmadd"]} errorProps={{ actionDisabled: true, actionDisabledReason: "Insufficient Permissions" }}>
        <VersionSummaryStripContainer
          programmeId={queryProgrammeId}
          versionId={queryVersionId}
          onAction={() => setIsAssignModuleModalOpen(true)}
          actionLabel="Assign Module"
          actionIcon="plus"
          actionDisabled={isLocked}
          actionDisabledReason={isLocked ? "This version's settings are locked" : undefined}
        />
      </PermissionsGate>

      <StyledTableWrapper>
        <VersionModulesToolbar hook={hook} />
        <VersionModulesTable hook={hook} />
      </StyledTableWrapper>

      <StyledBulkActionSpacer $active={hasSelection} $height={bulkActionsBarHeight} />

      <VersionModulesBulkActions hook={hook} barRef={bulkActionsBarRef} />

      <ConfirmationModal
        open={Boolean(hook.confirmation)}
        copy={
          hook.confirmation && {
            title: `Unlink ${hook.confirmation.ids.length} Module${hook.confirmation.ids.length === 1 ? "" : "s"}?`,
            description: "These modules will be removed from this version. This can't be undone.",
            label: "Unlink",
            icon: "unlink",
            tone: "destructive",
          }
        }
        onClose={() => hook.setConfirmation(null)}
        onConfirm={hook.runConfirmedUnlink}
      />

      {isAssignModuleModalOpen && (
        <Modal
          title="Assign Module"
          onClose={() => setIsAssignModuleModalOpen(false)}
          width="90%"
        >
          <AvailableTables
            data={availableData}
            refresh={handleAssignModuleRefresh}
            id={id}
            filterData={filterDataAssigned}
            loading={availableLoading}
          />
        </Modal>
      )}
    </div>
  );
};

export default AssignedTable;
